import React, { useState, useEffect, useMemo } from "react";
import { useDistrict } from "@/context/DistrictContext";
import { Reveal, FlatCard, SectionHeader, fadeUp } from "@/components/primitives";
import { motion } from "framer-motion";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    Sheet, SheetContent, SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
    Compass, BookOpen, Mountain, Landmark, Users, Flame, ScrollText,
    Store, Crown, Heart, Sparkles, Hammer, Leaf, Telescope, ChevronRight,
    Menu, ExternalLink, MapPin,
} from "lucide-react";
import HeritageSiteModal from "@/components/HeritageSiteModal";

const SECTIONS = [
    { id: "overview", label: "Overview", icon: Compass },
    { id: "about", label: "About the Initiative", icon: BookOpen },
    { id: "history", label: "History & Timeline", icon: ScrollText },
    { id: "geography", label: "Geography", icon: Mountain },
    { id: "heritage", label: "Heritage Sites", icon: Landmark },
    { id: "people", label: "Famous People", icon: Users },
    { id: "culture", label: "Culture & Traditions", icon: Flame },
    { id: "stories", label: "Famous Stories", icon: ScrollText },
    { id: "living", label: "Living District", icon: Store },
    { id: "women", label: "Key Women", icon: Crown },
    { id: "unsung", label: "Unsung Heroes", icon: Heart },
    { id: "contemporary", label: "Contemporary Achievers", icon: Sparkles },
    { id: "economy", label: "Economy & Craft", icon: Hammer },
    { id: "flora", label: "Flora & Fauna", icon: Leaf },
    { id: "vision", label: "Vision 2047", icon: Telescope },
];

const useDetail = () => {
    const [detail, setDetail] = useState(null);
    return { detail, open: !!detail, openDetail: setDetail, close: () => setDetail(null) };
};

const Sidebar = ({ active, onClick }) => (
    <nav className="space-y-1 font-data text-[12px]" data-testid="portal-sidebar">
        {SECTIONS.map((s) => {
            const Icon = s.icon;
            const isActive = active === s.id;
            return (
                <a
                    key={s.id}
                    href={`#${s.id}`}
                    data-testid={`sidebar-${s.id}`}
                    onClick={onClick}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors ${
                        isActive ? "bg-ink text-ivory" : "ink hover:bg-ivory-deep"
                    }`}
                >
                    <Icon className="w-3.5 h-3.5 shrink-0" />
                    <span className="tracking-[0.04em]">{s.label}</span>
                </a>
            );
        })}
    </nav>
);

const KpiCard = ({ label, value, unit, sub }) => (
    <FlatCard className="p-5 md:p-6 hover:border-[var(--ink)] transition-colors">
        <div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">{label}</div>
        <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-4xl md:text-5xl ink">{value}</span>
            {unit && <span className="font-data text-xs ink-mute">{unit}</span>}
        </div>
        {sub && <div className="font-data text-[11px] ink-mute mt-2">{sub}</div>}
    </FlatCard>
);

const DetailModal = ({ detail, open, onClose, accent }) => {
    if (!detail) return null;
    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="detail-modal">
                <DialogHeader>
                    <div className="font-data text-[10px] tracking-[0.22em] uppercase" style={{ color: accent }}>
                        {detail.kicker}
                    </div>
                    <DialogTitle className="font-display text-3xl md:text-4xl leading-tight mt-1">
                        {detail.title}
                    </DialogTitle>
                    {detail.subtitle && (
                        <DialogDescription className="font-body text-base ink-mute mt-2">
                            {detail.subtitle}
                        </DialogDescription>
                    )}
                </DialogHeader>
                <div className="editorial-rule my-3" />
                {detail.image && (
                    <div className="aspect-[16/9] overflow-hidden rounded-sm bg-ivory-deep">
                        <img src={detail.image} alt={detail.title} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                )}
                {detail.body && <p className="font-body text-base leading-relaxed mt-4">{detail.body}</p>}
                {detail.meta && (
                    <div className="grid grid-cols-2 gap-3 mt-5">
                        {detail.meta.map((m) => (
                            <div key={m.label} className="border hairline p-3">
                                <div className="font-data text-[10px] uppercase tracking-[0.18em] ink-mute">{m.label}</div>
                                <div className="font-display text-lg mt-1">{m.value}</div>
                            </div>
                        ))}
                    </div>
                )}
                {detail.cta && (
                    <a
                        href={detail.cta.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="detail-modal-cta"
                        className="mt-5 inline-flex items-center gap-2 font-data text-[11px] uppercase tracking-[0.18em] terra underline-reveal"
                    >
                        {detail.cta.label} <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                )}
            </DialogContent>
        </Dialog>
    );
};

const Portal = () => {
    const { district, loading } = useDistrict();
    const [active, setActive] = useState("overview");
    const { detail, open, openDetail, close } = useDetail();
    const [heritageSite, setHeritageSite] = useState(null);

    useEffect(() => {
        const onScroll = () => {
            const offset = 160;
            for (let i = SECTIONS.length - 1; i >= 0; i--) {
                const el = document.getElementById(SECTIONS[i].id);
                if (el && el.getBoundingClientRect().top - offset <= 0) {
                    setActive(SECTIONS[i].id);
                    break;
                }
            }
        };
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const overview = district?.overview;

    const overviewCards = useMemo(() => {
        if (!overview) return [];
        return [
            { label: "Area", value: overview.area_km2?.toLocaleString(), unit: "sq km" },
            { label: "Heritage Sites", value: overview.heritage_count, unit: "documented" },
            { label: "Literacy", value: overview.literacy_pct, unit: "%" },
            { label: "Rivers", value: overview.rivers, unit: "major flows" },
            { label: "Art Forms", value: overview.art_forms, unit: "living" },
            { label: "Freedom Fighters", value: overview.freedom_fighters, unit: "documented" },
        ];
    }, [overview]);

    if (loading || !district) {
        return (
            <div className="min-h-[60vh] grid place-items-center">
                <div className="font-display text-2xl italic ink-mute">Loading the district…</div>
            </div>
        );
    }

    const accent = district.accent;

    return (
        <div className="bg-ivory">
            {/* HERO */}
            <section id="overview" className="relative overflow-hidden">
                <div className="relative h-[78vh] md:h-[88vh] min-h-[560px]">
                    <img
                        src={district.hero_image}
                        alt={district.name}
                        className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/25 to-[var(--ivory)]" />
                    <div className="grain absolute inset-0" />

                    <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-end pb-16 md:pb-24">
                        <motion.div
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.2, 0.7, 0.2, 1] }}
                            className="max-w-4xl"
                        >
                            <div className="flex items-center gap-3 mb-6 text-white/85">
                                <span className="ornament" />
                                <span className="font-data text-[10px] tracking-[0.28em] uppercase">
                                    {district.state} · Established {district.established}
                                </span>
                            </div>
                            <h1
                                className="font-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-[88px] leading-[0.96] mb-6"
                                data-testid="hero-title"
                            >
                                {district.tagline}
                            </h1>
                            <p className="font-body text-white/90 text-lg md:text-xl italic">
                                {district.subtitle}
                            </p>
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-20 grid lg:grid-cols-12 gap-10">
                {/* Sidebar (desktop) */}
                <aside className="hidden lg:block lg:col-span-3">
                    <div className="sticky top-24">
                        <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mb-4">Contents</div>
                        <Sidebar active={active} />
                    </div>
                </aside>

                {/* Mobile contents trigger */}
                <div className="lg:hidden -mt-4">
                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="outline" className="border hairline gap-2 font-data text-xs uppercase tracking-[0.18em]" data-testid="open-sidebar-sheet">
                                <Menu className="w-4 h-4" /> Contents
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="left" className="bg-ivory w-72">
                            <div className="mt-8">
                                <Sidebar active={active} onClick={() => {}} />
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>

                <main className="lg:col-span-9 space-y-24 md:space-y-32">
                    {/* Overview KPIs */}
                    <section>
                        <SectionHeader
                            kicker="A First Person Account"
                            title={`I am ${district.name}.`}
                            lede={`From my soil rose ${overview.heritage_count} heritage memories, ${overview.art_forms} living art forms and the voices of ${overview.freedom_fighters} freedom fighters. This is my biography — written in stone, song and statistic.`}
                        />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                            {overviewCards.map((c, i) => (
                                <Reveal key={c.label} delay={i}>
                                    <KpiCard {...c} />
                                </Reveal>
                            ))}
                        </div>
                    </section>

                    {/* About the initiative */}
                    <section id="about" className="scroll-mt-24">
                        <SectionHeader
                            kicker="The Initiative"
                            title={district.about_initiative_headline || "India Has Never Built the Infrastructure That Carries Its Soul"}
                            lede=""
                        />
                        <div className="max-w-3xl space-y-5 mb-12">
                            {(district.about_initiative || "").split("\n\n").map((para, i) => (
                                <p key={i} className="font-body text-base md:text-lg ink-mute leading-relaxed">{para}</p>
                            ))}
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {[
                                { n: "01", t: "The Complete Map", d: "Physical, cultural, economic and historical layers — integrated as one." },
                                { n: "02", t: "Cultural Narrative Infrastructure", d: "Documenting living traditions, oral histories and indigenous knowledge." },
                                { n: "03", t: "Digital + Physical Preservation", d: "Heritage Cloud + printed encyclopaedia + audio podcasts." },
                                { n: "04", t: "Active Dissemination", d: "School curriculum, civic theatres, diaspora reach." },
                                { n: "05", t: "Civic Identity", d: "Pride, participation and informed self-governance." },
                                { n: "—", t: "First-Person Principle", d: "Every district speaks for itself — never spoken about." },
                            ].map((p, i) => (
                                <Reveal key={p.n} delay={i}>
                                    <FlatCard className="p-6 h-full">
                                        <div className="font-data text-xs tracking-[0.2em] terra mb-3">{p.n}</div>
                                        <div className="font-display text-2xl leading-tight mb-2">{p.t}</div>
                                        <p className="font-body text-sm ink-mute leading-relaxed">{p.d}</p>
                                    </FlatCard>
                                </Reveal>
                            ))}
                        </div>
                    </section>

                    {/* History */}
                    <section id="history" className="scroll-mt-24">
                        <SectionHeader
                            kicker="History"
                            title="A Timeline Carved In Time"
                            lede="From geological birth to independence and beyond — moments that defined this district."
                        />
                        <ol className="relative border-l hairline pl-6 space-y-8">
                            {district.history_timeline.map((h, i) => (
                                <Reveal key={i} delay={i}>
                                    <li className="relative">
                                        <span className="absolute -left-[31px] top-2 w-2 h-2 rounded-full" style={{ background: accent }} />
                                        <div className="font-data text-xs tracking-[0.2em] uppercase" style={{ color: accent }}>{h.year}</div>
                                        <div className="font-display text-2xl md:text-3xl mt-1 mb-2">{h.title}</div>
                                        <p className="font-body text-base ink-mute leading-relaxed max-w-2xl">{h.description}</p>
                                    </li>
                                </Reveal>
                            ))}
                        </ol>
                    </section>

                    {/* Geography */}
                    <section id="geography" className="scroll-mt-24">
                        <SectionHeader
                            kicker="Geography"
                            title="The Land I Stand On"
                            lede={district.geography.description}
                        />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <FlatCard className="p-5"><div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">Terrain</div><div className="font-display text-lg mt-1">{district.geography.terrain}</div></FlatCard>
                            <FlatCard className="p-5"><div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">Climate</div><div className="font-display text-lg mt-1">{district.geography.climate}</div></FlatCard>
                            <FlatCard className="p-5"><div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">Area</div><div className="font-display text-3xl mt-1">{district.geography.area_km2.toLocaleString()} <span className="text-base ink-mute">km²</span></div></FlatCard>
                            <FlatCard className="p-5"><div className="font-data text-[10px] tracking-[0.2em] uppercase ink-mute">Elevation</div><div className="font-display text-3xl mt-1">{district.geography.elevation_m} <span className="text-base ink-mute">m</span></div></FlatCard>
                        </div>
                        <div className="mt-5 flex flex-wrap gap-2" data-testid="rivers-list">
                            {district.geography.rivers.map((r) => (
                                <span key={r} className="chip">~ {r}</span>
                            ))}
                        </div>
                    </section>

                    {/* Heritage Sites */}
                    <section id="heritage" className="scroll-mt-24">
                        <SectionHeader
                            kicker="Heritage"
                            title="Stones That Remember"
                            lede={`${district.overview.heritage_count} documented sites. Each carries a story, a fracture, a restoration.`}
                        />
                        <div className="grid md:grid-cols-2 gap-6">
                            {district.heritage_sites.map((s, i) => (
                                <Reveal key={s.name} delay={i}>
                                    <button
                                        data-testid={`heritage-card-${i}`}
                                        onClick={() => setHeritageSite(s)}
                                        className="text-left w-full group block bg-white hairline border rounded-md overflow-hidden hover:border-[var(--ink)] transition-colors"
                                    >
                                        <div className="aspect-[16/10] overflow-hidden bg-ivory-deep">
                                            <img src={s.image} alt={s.name} loading="lazy"
                                                 className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        </div>
                                        <div className="p-5">
                                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute">
                                                {s.type} · {s.era}
                                            </div>
                                            <div className="font-display text-2xl mt-1.5 leading-tight">{s.name}</div>
                                            <p className="font-body text-sm ink-mute mt-2 line-clamp-2">{s.significance}</p>
                                        </div>
                                    </button>
                                </Reveal>
                            ))}
                        </div>
                    </section>

                    {/* Famous People */}
                    <Grid section="people" kicker="Sons & Daughters" title="The People Who Made The Soil" lede="Saints, statesmen, scholars and singers who walked these streets." items={district.famous_people}
                          accent={accent} openDetail={openDetail}
                          render={(p) => ({
                              kicker: `${p.era} · ${p.domain}`, title: p.name, subtitle: p.domain, body: p.contribution,
                              meta: [{ label: "Era", value: p.era }, { label: "Domain", value: p.domain }],
                          })} />

                    {/* Culture */}
                    <Grid section="culture" kicker="Culture & Traditions" title="Festivals, Rituals & Folk Memory" lede="Annual cycles and daily devotions that hold the city together." items={district.culture}
                          accent={accent} openDetail={openDetail}
                          render={(c) => ({
                              kicker: `${c.type} · ${c.when}`, title: c.name, body: c.description,
                              meta: [{ label: "Type", value: c.type }, { label: "When", value: c.when }],
                          })} />

                    {/* Stories */}
                    <Grid section="stories" kicker="Famous Stories" title="Myths, Legends & Oral Lore" lede="Stories the elders tell — when stones forget, songs remember." items={district.famous_stories}
                          accent={accent} openDetail={openDetail}
                          render={(s) => ({ kicker: s.type, title: s.title, body: s.description })} />

                    {/* Living District */}
                    <Grid section="living" kicker="The Living District" title="Streets That Still Trade" lede="Shops, food stalls, saree houses — daily heritage you can still touch." items={district.living_district}
                          accent={accent} openDetail={openDetail}
                          render={(l) => ({
                              kicker: `${l.type} · Founded ${l.founded}`, title: l.name, body: l.description,
                              meta: [{ label: "Founded", value: l.founded }, { label: "Type", value: l.type }],
                          })} />

                    {/* Key Women */}
                    <Grid section="women" kicker="Key Women" title="Names Returning To The Page" lede="Restoring the women whose contributions were once erased." items={district.key_women}
                          accent={accent} openDetail={openDetail}
                          render={(w) => ({ kicker: w.era, title: w.name, body: w.contribution })} />

                    {/* Unsung Heroes */}
                    <Grid section="unsung" kicker="Unsung Heroes" title="Quiet Custodians" lede="The seed-saver, the schoolteacher, the last master — they are the district's true keepers." items={district.unsung_heroes}
                          accent={accent} openDetail={openDetail}
                          render={(u) => ({ kicker: u.profession, title: u.name, body: u.description })} />

                    {/* Contemporary Achievers */}
                    <Grid section="contemporary" kicker="Contemporary Achievers" title="The District Today" lede="Living artists, athletes, scientists and statesmen making India proud now." items={district.contemporary_achievers}
                          accent={accent} openDetail={openDetail}
                          render={(c) => ({ kicker: c.field, title: c.name, body: c.achievement })} />

                    {/* Economy & Craft */}
                    <Grid section="economy" kicker="Economy & Craft" title="What These Hands Make" lede="GI-tagged crafts, world-renowned industries, generational livelihoods." items={district.economy_craft}
                          accent={accent} openDetail={openDetail} columns={2}
                          render={(e) => ({
                              kicker: `${e.type} · ${e.tag}`, title: e.name, body: e.description,
                              meta: [{ label: "Type", value: e.type }, { label: "Status", value: e.tag }],
                          })} />

                    {/* Flora & Fauna */}
                    <section id="flora" className="scroll-mt-24">
                        <SectionHeader kicker="Flora & Fauna" title="What Lives Beside Me" lede="The trees, the birds, the rivers' companions." />
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                            {district.flora_fauna.map((f, i) => (
                                <Reveal key={f.name} delay={i}>
                                    <FlatCard className="p-5 h-full">
                                        <div className="font-data text-[10px] tracking-[0.22em] uppercase" style={{ color: accent }}>{f.type}</div>
                                        <div className="font-display text-xl md:text-2xl mt-1.5 leading-tight">{f.name}</div>
                                        <p className="font-body text-sm ink-mute mt-2 leading-relaxed">{f.description}</p>
                                    </FlatCard>
                                </Reveal>
                            ))}
                        </div>
                    </section>

                    {/* Vision 2047 */}
                    <section id="vision" className="scroll-mt-24">
                        <SectionHeader kicker="Vision 2047" title="What I Mean To Become" lede="A 22-year roadmap toward a Viksit district — and a Viksit Bharat." />
                        <div className="space-y-4">
                            {district.vision_2047.map((v, i) => (
                                <Reveal key={v.target_area} delay={i}>
                                    <FlatCard className="p-6 md:p-8 grid md:grid-cols-12 gap-5 items-start">
                                        <div className="md:col-span-3">
                                            <div className="font-data text-[10px] tracking-[0.22em] uppercase terra">Target Area</div>
                                            <div className="font-display text-2xl mt-1">{v.target_area}</div>
                                        </div>
                                        <div className="md:col-span-3">
                                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute">Today</div>
                                            <div className="font-body text-base mt-1">{v.current}</div>
                                        </div>
                                        <div className="md:col-span-3">
                                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute">By 2047</div>
                                            <div className="font-body text-base mt-1">{v.target}</div>
                                        </div>
                                        <div className="md:col-span-3">
                                            <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute">Action Plan</div>
                                            <div className="font-body text-sm mt-1 ink-mute">{v.action_plan}</div>
                                        </div>
                                    </FlatCard>
                                </Reveal>
                            ))}
                        </div>
                    </section>

                    {/* Closing message */}
                    <section className="text-center py-10">
                        <div className="ornament-vert ink-mute" />
                        <h3 className="font-display italic text-3xl md:text-5xl leading-tight max-w-3xl mx-auto mt-6">
                            “I am older than history, older than tradition,<br />older even than legend.”
                        </h3>
                        <div className="font-data text-[10px] tracking-[0.22em] uppercase ink-mute mt-5">
                            — {district.name} speaks
                        </div>
                    </section>
                </main>
            </div>

            <DetailModal detail={detail} open={open} onClose={close} accent={accent} />
            <HeritageSiteModal
                site={heritageSite}
                open={!!heritageSite}
                onClose={() => setHeritageSite(null)}
                accent={accent}
                districtName={district.name}
            />
        </div>
    );
};

const Grid = ({ section, kicker, title, lede, items, accent, openDetail, render, columns = 3 }) => (
    <section id={section} className="scroll-mt-24">
        <SectionHeader kicker={kicker} title={title} lede={lede} />
        <div className={`grid sm:grid-cols-2 ${columns === 2 ? "lg:grid-cols-2" : "lg:grid-cols-3"} gap-5`}>
            {items.map((item, i) => {
                const meta = render(item);
                return (
                    <Reveal key={`${section}-${i}`} delay={i}>
                        <button
                            data-testid={`${section}-card-${i}`}
                            onClick={() => openDetail(meta)}
                            className="text-left w-full p-6 h-full bg-white hairline border rounded-md hover:border-[var(--ink)] transition-all group"
                        >
                            <div className="font-data text-[10px] tracking-[0.22em] uppercase" style={{ color: accent }}>
                                {meta.kicker}
                            </div>
                            <div className="font-display text-2xl mt-2 leading-tight">{meta.title}</div>
                            <p className="font-body text-sm ink-mute mt-3 leading-relaxed line-clamp-3">{meta.body}</p>
                            <ChevronRight className="w-4 h-4 mt-4 ink-mute group-hover:terra group-hover:translate-x-1 transition-all" />
                        </button>
                    </Reveal>
                );
            })}
        </div>
    </section>
);

export default Portal;
